/**
 * Apply to GitHub (#1522): the room's decisions as GitHub changes. Each one is
 * shown with GitHub's current value and written only if ticked.
 */

import { useEffect, useState, type ReactElement } from 'react'
import {
  Badge,
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowUpload20Regular,
  CalendarLtr16Regular,
  Checkmark16Regular,
  Comment16Regular,
  Dismiss16Regular,
  Person16Regular,
} from '@fluentui/react-icons'
import { useToast } from '../ToastProvider'
import { useQueryClient } from '@tanstack/react-query'
import { cycleKey, planningEditKey, usePlanningSaveState, useRefreshPlan, useWriteBack, useWriteBackPreview } from '../../hooks/usePlanning'
import { apiErrorMessage } from '../../lib/apiError'
import { summaryText, writebackRefusedBeforeWriting } from '../../lib/planning'
import type { Plan, PlanningGitHubAccess, PlanState, WriteBackChange, WriteBackResponse } from '../../types/planning'
import { InfoHint } from './PlanParts'

const GROUPS: { kind: WriteBackChange['kind']; title: string; icon: ReactElement }[] = [
  { kind: 'cycle', title: 'Cycle', icon: <CalendarLtr16Regular /> },
  { kind: 'assignees', title: 'Owner', icon: <Person16Regular /> },
]

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  groupHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  change: { color: tokens.colorNeutralForeground2 },
  issueInput: { width: '7em' },
  comment: { width: '100%' },
  ok: { color: tokens.colorPaletteGreenForeground1 },
  bad: { color: tokens.colorPaletteRedForeground1 },
})

const short = (t: string) => t.replace(/^\[[^\]]+\]\s*/, '')

function changeText(c: WriteBackChange): string {
  return c.kind === 'comment'
    ? `Read-back on #${c.issue}`
    : `#${c.issue} ${short(c.title)}: ${c.before} → ${c.after}`
}

/** The hand-set ticks without the read-back's. */
function withoutReadBackTick(prev: Record<string, boolean>): Record<string, boolean> {
  return Object.fromEntries(Object.entries(prev).filter(([id]) => !id.startsWith('comment:')))
}

export function WriteBackDialog({
  cycle,
  version,
  plan,
  state,
  github,
}: {
  cycle: string
  /** The plan version on the page now; a preview from an older one is not applied. */
  version: number
  plan: Plan
  state: PlanState
  github?: PlanningGitHubAccess
}) {
  const styles = useStyles()
  const { showToast } = useToast()
  const [open, setOpen] = useState(false)
  const [commentIssue, setCommentIssue] = useState<number | undefined>()
  const [issueText, setIssueText] = useState('')
  // Ticks the admin set by hand, kept across previews in one sitting (Copilot on #1566).
  const [decided, setDecided] = useState<Record<string, boolean>>({})
  // The read-back as edited; null follows the plan, so it says what Apply writes.
  const [edited, setEdited] = useState<string | null>(null)
  const comment = edited ?? summaryText(plan, state, true)
  // What was sent is kept with the answer: the preview may have moved on since.
  const [result, setResult] = useState<{ out: WriteBackResponse; sent: WriteBackChange[] } | null>(
    null,
  )
  const save = usePlanningSaveState(cycle)
  const allowed = github?.enabled === true
  const preview = useWriteBackPreview(cycle, allowed && open && !result && !save.pending, commentIssue)
  const write = useWriteBack(cycle)
  const qc = useQueryClient()
  const refresh = useRefreshPlan(cycle)

  useEffect(() => {
    if (preview.data) {
      setIssueText(preview.data.comment_issue ? String(preview.data.comment_issue) : '')
    }
  }, [preview.data])

  // The page or the preview moved to another version of the plan, by Read
  // again or by the page's own poll catching up: a tick on the read-back was
  // given to the text as it was, so it is asked for again (Copilot on #1589).
  // The edited text stays: it is the admin's typing.
  const previewVersion = preview.data?.version
  useEffect(() => {
    setDecided(withoutReadBackTick)
  }, [version, previewVersion])

  const changes = preview.data?.changes ?? []
  const unmapped = Object.entries(preview.data?.unmapped ?? {})
  const leftOut = preview.data?.left_out ?? []
  // Untouched changes start ticked unless there is something to look at first.
  // The read-back is a post, so it is only sent when ticked by hand.
  const isTicked = (c: WriteBackChange) => decided[c.id] ?? (!c.conflict && c.kind !== 'comment')
  const ticked = changes.filter(isTicked)
  const commentChange = changes.find((c) => c.kind === 'comment')
  const posting = !!commentChange && isTicked(commentChange)
  const edits = ticked.length - (posting ? 1 : 0)
  // The page and the preview read different versions of the plan. The read-back
  // is built from the page's, so either way round they must match (Copilot on #1583).
  const outdated = !!preview.data && !preview.isPlaceholderData && version !== preview.data.version
  // Only a preview GitHub just answered can be applied: while another issue is
  // being read, the list on screen still names the old one (Copilot on #1571).
  const current =
    !!preview.data && !preview.isFetching && !preview.isPlaceholderData && !preview.error && !outdated

  const onApply = async () => {
    if (!allowed || qc.isMutating({ mutationKey: planningEditKey(cycle), exact: true })) return
    try {
      const out = await write.mutateAsync({
        ids: ticked.map((c) => c.id),
        comment_issue: preview.data?.comment_issue ?? undefined,
        comment,
        version: preview.data?.version,
      })
      setResult({ out, sent: ticked })
      // Skipped ones were not written either (Copilot on #1566).
      const missed = out.results.filter((r) => !r.ok).length + out.skipped.length
      showToast({
        title: missed ? `${missed} change${missed === 1 ? '' : 's'} not written` : 'Written to GitHub',
        intent: missed ? 'warning' : 'success',
      })
      // A GitHub-built draft re-reads the board; an imported file is left as it is.
      if (plan.source === 'github') refresh.mutate()
    } catch (err) {
      // A timeout can come after GitHub took some of the writes.
      showToast(
        writebackRefusedBeforeWriting(err)
          ? { title: 'Nothing written', body: apiErrorMessage(err), intent: 'error' }
          : { title: 'May be partly written', body: 'Check GitHub before trying again.', intent: 'error' },
      )
    }
  }

  const readAgain = () => {
    // The tick goes when the new version arrives (the effect above).
    void preview.refetch()
    // The page may be the one behind; its poll would catch up within seconds.
    void qc.invalidateQueries({ queryKey: cycleKey(cycle) })
  }

  const commitIssue = () => {
    const n = Number(issueText)
    if (Number.isInteger(n) && n > 0 && n !== preview.data?.comment_issue) setCommentIssue(n)
  }

  const openDialog = () => {
    setDecided({})
    setEdited(null)
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    setResult(null)
    setCommentIssue(undefined)
  }

  const row = (c: WriteBackChange) => (
    <div key={c.id} className={styles.row}>
      <Checkbox
        checked={isTicked(c)}
        onChange={(_, d) => setDecided((prev) => ({ ...prev, [c.id]: !!d.checked }))}
        label={c.kind === 'comment' ? `Post read-back on #${c.issue}` : `#${c.issue} ${short(c.title)}`}
      />
      {c.kind !== 'comment' && (
        <Caption1 className={styles.change}>
          {c.before} → {c.after}
        </Caption1>
      )}
      {c.conflict && (
        <Badge appearance="tint" color="warning">
          {c.conflict}
        </Badge>
      )}
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={(_, d) => (d.open ? openDialog() : close())}>
      <DialogTrigger disableButtonEnhancement>
        <Button
          icon={<ArrowUpload20Regular />}
          disabled={!allowed || !!save.pending}
          title={!allowed ? github?.reason ?? 'GitHub availability is not yet known.' : undefined}
        >Apply to GitHub</Button>
      </DialogTrigger>
      <DialogSurface aria-describedby={undefined}>
        <DialogBody>
          {/* The hint sits in the action slot, outside the heading's accessible name. */}
          <DialogTitle
            action={
              <InfoHint text="Only ticked changes are written, under the account that owns the GitHub token. GitHub is read again first; anything changed there since this preview is left alone." />
            }
          >
            Apply to GitHub
          </DialogTitle>
          <DialogContent className={styles.body}>
            {result ? (
              <Results result={result.out} sent={result.sent} />
            ) : preview.isLoading ? (
              <Spinner size="small" label="Reading GitHub…" />
            ) : preview.error ? (
              <MessageBar intent="error">
                <MessageBarBody>{apiErrorMessage(preview.error, 'Could not read GitHub.')}</MessageBarBody>
              </MessageBar>
            ) : (
              <>
                {outdated && (
                  <MessageBar intent="warning">
                    <MessageBarBody>The plan changed since this preview.</MessageBarBody>
                    <MessageBarActions>
                      <Button size="small" onClick={readAgain}>
                        Read again
                      </Button>
                    </MessageBarActions>
                  </MessageBar>
                )}
                {unmapped.length > 0 && (
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      No GitHub login for:{' '}
                      {unmapped.map(([name, why]) => `${name} (${why})`).join(', ')}.{' '}
                      <InfoHint text="Set each person's GitHub handle under Admin → All users → Edit, then open this again." />
                    </MessageBarBody>
                  </MessageBar>
                )}
                {leftOut.length > 0 && (
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      Not written:{' '}
                      {leftOut.map((x) => `#${x.issue} ${short(x.title)} (${x.why})`).join(', ')}.{' '}
                      <InfoHint text="Add the issue, or the next cycle, to the board on GitHub, then open this again." />
                    </MessageBarBody>
                  </MessageBar>
                )}
                {changes.every((c) => c.kind === 'comment') && leftOut.length === 0 && !posting && (
                  <Caption1>The board already matches the room.</Caption1>
                )}
                {GROUPS.map((g) => {
                  const rows = changes.filter((c) => c.kind === g.kind)
                  if (rows.length === 0) return null
                  return (
                    <div key={g.kind} className={styles.group} data-testid={`wb-${g.kind}`}>
                      <div className={styles.groupHead}>
                        <span aria-hidden="true">{g.icon}</span>
                        {g.title}
                      </div>
                      {rows.map(row)}
                    </div>
                  )
                })}
                {/* Always here, so a cycle with no [Plan] issue can still name one. */}
                <div className={styles.group} data-testid="wb-comment">
                  <div className={styles.groupHead}>
                    <span aria-hidden="true">
                      <Comment16Regular />
                    </span>
                    Read-back
                  </div>
                  {commentChange ? (
                    row(commentChange)
                  ) : (
                    <Caption1>No [Plan] issue on the page. Name one to post it.</Caption1>
                  )}
                  <div className={styles.row}>
                    <Caption1>Issue</Caption1>
                    <Input
                      className={styles.issueInput}
                      size="small"
                      type="number"
                      aria-label="Issue for the read-back"
                      value={issueText}
                      onChange={(_, d) => setIssueText(d.value)}
                      onBlur={commitIssue}
                      onKeyDown={(e) => e.key === 'Enter' && commitIssue()}
                    />
                  </div>
                  <Textarea
                    className={styles.comment}
                    aria-label="Read-back to post"
                    resize="vertical"
                    rows={6}
                    value={comment}
                    onChange={(_, d) => setEdited(d.value)}
                  />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{result ? 'Close' : 'Cancel'}</Button>
            </DialogTrigger>
            {!result && (
              <Button
                appearance="primary"
                disabled={!allowed || !!save.pending || ticked.length === 0 || write.isPending || !current}
                onClick={() => void onApply()}
              >
                {write.isPending
                  ? 'Writing…'
                  : posting
                    ? edits
                      ? `Apply ${edits} + read-back`
                      : 'Post read-back'
                    : `Apply ${edits}`}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

function Results({ result, sent }: { result: WriteBackResponse; sent: WriteBackChange[] }) {
  const styles = useStyles()
  const byId = new Map(sent.map((c) => [c.id, c]))
  return (
    <>
      {result.results.map((r) => {
        const c = byId.get(r.id)
        return (
          <div key={r.id} className={styles.row}>
            {r.ok ? (
              <Checkmark16Regular className={styles.ok} aria-label="Written" />
            ) : (
              <Dismiss16Regular className={styles.bad} aria-label="Not written" />
            )}
            <span>{c ? changeText(c) : r.id}</span>
            {r.message && <Caption1 className={styles.bad}>{r.message}</Caption1>}
          </div>
        )
      })}
      {result.skipped.length > 0 && (
        <Caption1>
          {result.skipped.length} changed on GitHub since the preview, so left alone.
        </Caption1>
      )}
    </>
  )
}
